import * as ko from "knockout";
import {global} from "../../appContext";
import {RouteValidator} from "../../auth/RouteValidator";
import {nsDom} from "../../util/dom";
import {storeObservable} from "../../util/sessionstore";
import {hubSpotSupport} from "../../tracking/HubSpotSupport";
import {toggleRightPanel} from "../../util/rightPanel";
import detailsHtml from './details.html?raw';

export let template = detailsHtml;

export class viewModel {
    private readonly rv: RouteValidator = global.routeValidator;
    private contentHandler = this.onContentScroll.bind(this);
    private readonly windowHandler = this.updateLayoutCssVars.bind(this);
    private resizeObserver?: ResizeObserver;
    private isScrolled: KnockoutObservable<boolean> = ko.observable(false);
    private readonly rightPanelToggle: KnockoutObservable<boolean> = storeObservable('layout.right.toggle');
    private readonly rightPanelMode: KnockoutObservable<'profile' | 'chat'> = storeObservable('layout.right.mode');

    readonly showFabSupport = ko.pureComputed((): boolean =>
        global.login.isLoggedIn()
        && !global.login.isProcessing()
        && hubSpotSupport.isChatAvailable()
    );

    readonly isChatPanelActive = ko.pureComputed((): boolean =>
        !!this.rightPanelToggle() && this.rightPanelMode() === 'chat'
    );

    readonly fabSupportLabel = ko.pureComputed((): string => 'Support');

    readonly toggleRightPanel = toggleRightPanel;

    constructor() {
        this.initListeners();
    }

    protected initListeners() {
        window.requestAnimationFrame(() => this.tryInitialize());
    }

    protected tryInitialize() {
        const elRoot = document.querySelector(':root');
        const elHeader = document.querySelector('.lh > *');
        const elLeft = document.querySelector('.lm-l > *');
        const elRight = document.querySelector('.lm-r > *');
        const elContent = document.querySelector('.lm-c');

        if (!(elRoot && elHeader && elLeft && elRight && elContent)) {
            this.initListeners();
            return;
        }

        /* Listen to content container scroll, not window scroll */
        elContent.addEventListener('scroll', this.contentHandler);
        window.addEventListener('resize', this.windowHandler);
        this.resizeObserver = new ResizeObserver(() => this.updateLayoutCssVars());
        this.resizeObserver.observe(elHeader);
        this.resizeObserver.observe(elLeft);
        this.resizeObserver.observe(elRight);
        window.requestAnimationFrame(() => {
            this.updateLayoutCssVars();
            this.onContentScroll();
        });
    }

    private updateLayoutCssVars() {
        requestAnimationFrame(() => {
            const elHeader = document.querySelector('.lh');
            const elLeft = document.querySelector('.lm-l');
            const elRight = document.querySelector('.lm-r');
            const r = document.querySelector(':root') as HTMLElement;

            if (!(elHeader && elLeft && elRight && r)) {
                return;
            }

            const rectHeader = elHeader.getBoundingClientRect();
            const rectLeft = elLeft.getBoundingClientRect();
            const rectRight = elRight.getBoundingClientRect();

            r.style.setProperty('--header-top', rectHeader.top + 'px');
            r.style.setProperty('--header-height', rectHeader.height + 'px');
            r.style.setProperty('--footer-top', '100vh');
            r.style.setProperty('--footer-bottom', '100vh');
            r.style.setProperty('--footer-height', '0px');
            r.style.setProperty('--rpanel-left', rectRight.left + 'px');
            r.style.setProperty('--rpanel-right', rectRight.right + 'px');
            r.style.setProperty('--rpanel-width', rectRight.width + 'px');
            r.style.setProperty('--lpanel-width', rectLeft.width + 'px');
            r.style.setProperty('--lpanel-right', rectLeft.right + 'px');
        });
    }

    private onContentScroll() {
        requestAnimationFrame(() => {
            const elHeader = document.querySelector('.lh');
            const elContent = document.querySelector('.lm-c');
            const r = document.querySelector(':root') as HTMLElement;
            
            if (!elContent) return;
            
            /* Check if content has scrolled */
            const hasScrolled = elContent.scrollTop > 0;
            this.isScrolled(hasScrolled);
            
            if (hasScrolled) {
                elHeader?.classList.add('has-scrolled');
                elContent.classList.add('has-content-scrolled');
                r?.classList.add('g--is-scrolled');
            } else {
                elHeader?.classList.remove('has-scrolled');
                elContent.classList.remove('has-content-scrolled');
                r?.classList.remove('g--is-scrolled');
            }
        });
    }

    private killListeners() {
        const elContent = document.querySelector('.lm-c');
        if (elContent) {
            elContent.removeEventListener('scroll', this.contentHandler);
        }
        window.removeEventListener('resize', this.windowHandler);
        this.resizeObserver?.disconnect();
    }

    private onScrollToTopClicked = () => {
        const elContent = document.querySelector('.lm-c');
        if (elContent) {
            elContent.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    dispose() {
        this.killListeners();
        delete this.resizeObserver;
    }
}
